import { PICKUP_IN_PROGRESS_STATUSES } from "../constants";

/*
 * The board's On site column: every car the valet still holds, split by what
 * the desk has to do about it.
 */

export const ON_SITE_TABS = [
  { id: "requested", label: "Requested" },
  { id: "leaving", label: "Leaving" },
  { id: "attention", label: "Attention" },
  { id: "unknown", label: "Unknown" },
  { id: "all", label: "All" },
];

/* How far ahead "leaving" looks. Beyond it there is nothing to line up for. */
export const LEAVING_WINDOW_MS = 60 * 60 * 1000;

/* A pickup nobody has finished this long after it was asked for. */
export const PICKUP_LATE_MS = 20 * 60 * 1000;

export const REQUESTED_STATUSES = [
  "REQUESTED",
  ...PICKUP_IN_PROGRESS_STATUSES,
];

export const ON_SITE_STATUSES = [
  "CLAIMED",
  "PARKING",
  "PARKED",
  ...REQUESTED_STATUSES,
];

export const ATTENTION = {
  /* The guest said they would be gone by now and the car is still here. */
  DEPARTURE: "departure",
  /* The window they paid for has run out. */
  WINDOW: "window",
  /* A runner has had this pickup too long. */
  LATE: "late",
  /* At the door with a balance still on the booking. */
  UNPAID: "unpaid",
};

const time = (value) => {
  if (!value) return null;

  const ms = new Date(value).getTime();

  return Number.isFinite(ms) ? ms : null;
};

const isParked = (car) => car?.status === "PARKED";

const isRequested = (car) => REQUESTED_STATUSES.includes(car?.status);

/* Asked for, but nobody has the keys yet. */
const isQueued = (car) =>
  car?.status === "REQUESTED" || car?.status === "RUNNER_ASSIGNED";

const passed = (value, now) => {
  const at = time(value);

  return at !== null && at <= now;
};

/**
 * Why this car needs the desk, soonest problem first. Empty when it does not.
 */
export function attentionReasons(car, now = Date.now()) {
  const reasons = [];

  if (isParked(car)) {
    if (passed(car.expectedDeparture, now)) reasons.push(ATTENTION.DEPARTURE);
    if (passed(car.checkOut, now)) reasons.push(ATTENTION.WINDOW);
  }

  if (isRequested(car) && passed(car.requestedAt, now - PICKUP_LATE_MS)) {
    reasons.push(ATTENTION.LATE);
  }

  /* Payment stops a handover, so it is only the desk's problem once the car is
   * standing at the door. */
  if (
    car?.status === "READY" &&
    car?.paymentRequired !== false &&
    car?.paid !== true
  ) {
    reasons.push(ATTENTION.UNPAID);
  }

  return reasons;
}

/**
 * When this car stops being someone's plan and starts being their problem.
 * Drives both the Leaving order and the Attention order.
 */
export function deadlineOf(car, now = Date.now()) {
  if (isRequested(car)) return time(car.requestedAt);

  const candidates = [
    time(car?.expectedDeparture),
    time(car?.checkOut),
  ].filter((value) => value !== null);

  if (candidates.length === 0) return null;

  const overdue = candidates.filter((value) => value <= now);

  /* Two clocks can be past at once; the earlier one is the older problem. */
  return overdue.length > 0 ? Math.min(...overdue) : Math.min(...candidates);
}

/* A parked car the guest expects to take soon, or a pickup booked for soon. */
export function isLeavingSoon(car, now = Date.now()) {
  const at = isParked(car)
    ? time(car.expectedDeparture)
    : isQueued(car)
    ? time(car.requestedAt)
    : null;

  return at !== null && at > now && at <= now + LEAVING_WINDOW_MS;
}

/** A parked car nobody has committed to a departure for. */
export function isUnknownDeparture(car) {
  return isParked(car) && !car.expectedDeparture;
}

const byDeadline = (now) => (a, b) =>
  (deadlineOf(a, now) ?? Infinity) - (deadlineOf(b, now) ?? Infinity);

/* Longest on the lot first: those are the ones the desk should be chasing. */
const byParkedAt = (a, b) =>
  new Date(a.parkedAt ?? 0).getTime() - new Date(b.parkedAt ?? 0).getTime();

/**
 * Split the on-site cars into the board's tabs in one pass.
 */
export function splitOnSite(cars = [], now = Date.now()) {
  const requested = [];
  const leaving = [];
  const attention = [];
  const unknown = [];
  const all = [];

  cars.forEach((car) => {
    const reasons = attentionReasons(car, now);
    const row = { ...car, attentionReasons: reasons };

    all.push(row);
    if (reasons.length > 0) attention.push(row);
    if (isRequested(car)) requested.push(row);
    if (isLeavingSoon(car, now)) leaving.push(row);
    if (isUnknownDeparture(car)) unknown.push(row);
  });

  return {
    requested: requested.sort(byDeadline(now)),
    leaving: leaving.sort(byDeadline(now)),
    attention: attention.sort(byDeadline(now)),
    unknown: unknown.sort(byParkedAt),
    all: all.sort(byDeadline(now)),
  };
}

/**
 * Requested pickups no runner has taken. The badge counts them; nothing here
 * waits on a threshold, because one unassigned car is already worth seeing.
 */
export function unassignedCount(cars = []) {
  return cars.filter((car) => car.status === "REQUESTED" && !car.pickedBy)
    .length;
}
